import React, { useState } from "react";
import { Form, Button, Modal, Checkbox, Message, Icon } from "semantic-ui-react";
import { determineWasteUnits } from "../../../../utils/dataAndFormulas/wasteForms";
import { useLanguage } from "../../../../contexts";
import "./UpdateUnitsModal.scss";

export function UpdateUnitsModal(props) {
  const { show, onClose, onApply, currentUnits, registeredFields, isLoading } = props;
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [selectedFields, setSelectedFields] = useState({});
  const [fieldUnits, setFieldUnits] = useState({});

  const allWasteFields = [
    { key: "paper_and_cardboard", label: t("paper_and_cardboard") },
    { key: "plastic", label: t("plastic") },
    { key: "fabric_scraps", label: t("fabric_scraps") },
    { key: "metal_clean", label: t("metal_clean") },
    { key: "wood", label: t("wood") },
    { key: "food_waste", label: t("food_waste") },
    { key: "general_trash", label: t("general_trash") },
    { key: "glass", label: t("glass") },
    { key: "leather", label: t("leather") },
    { key: "foam_eva", label: t("foam_eva") },
    { key: "empty_cans_or_containers", label: t("empty_cans_or_containers") },
    { key: "rubber", label: t("rubber") },
    { key: "other_non_hazardous_waste", label: t("other_non_hazardous_waste") },
    { key: "batteries", label: t("batteries") },
    { key: "electronics", label: t("electronics") },
    { key: "drums_or_containers_with_product_remains", label: t("drums_or_containers_with_product_remains") },
    { key: "hospital_waste", label: t("hospital_waste") },
    { key: "fluorescent_tubes", label: t("fluorescent_tubes") },
    { key: "cooking_oil", label: t("cooking_oil") },
    { key: "maintenance_oil", label: t("maintenance_oil") },
    { key: "lubricants", label: t("lubricants") },
    { key: "paint_waste", label: t("paint_waste") },
    { key: "coal_ashes", label: t("coal_ashes") },
    { key: "rubber_hazardous", label: t("rubber_hazardous") },
    { key: "chemicals", label: t("chemicals") },
    { key: "contaminated_materials", label: t("contaminated_materials") },
    { key: "ink_cartridges", label: t("ink_cartridges") },
    { key: "medical_waste", label: t("medical_waste") },
    { key: "other_hazardous_waste", label: t("other_hazardous_waste") },
  ];

  const wasteFields =
    registeredFields && registeredFields.length > 0
      ? registeredFields
          .filter((field) => field.active !== false)
          .map((field) => ({
            key: field.name,
            label: t(field.name),
            code: field.code,
          }))
      : allWasteFields;

  const handleFieldToggle = (fieldKey, checked) => {
    setSelectedFields(prev => ({ ...prev, [fieldKey]: checked }));
    if (checked && !fieldUnits[fieldKey]) {
      setFieldUnits(prev => ({ ...prev, [fieldKey]: currentUnits?.[fieldKey] || "" }));
    }
  };

  const handleUnitChange = (fieldKey, unit) => {
    setFieldUnits(prev => ({ ...prev, [fieldKey]: unit }));
  };

  const handleSelectAll = () => {
    const allSelected = {};
    const allUnits = {};
    wasteFields.forEach(field => {
      allSelected[field.key] = true;
      allUnits[field.key] = currentUnits?.[field.key] || "";
    });
    setSelectedFields(allSelected);
    setFieldUnits(allUnits);
  };

  const handleDeselectAll = () => {
    setSelectedFields({});
  };

  const handleApply = () => {
    const unitsToApply = {};
    Object.keys(selectedFields).forEach(key => {
      if (selectedFields[key] && fieldUnits[key]) {
        unitsToApply[key] = fieldUnits[key];
      }
    });
    if (Object.keys(unitsToApply).length === 0) return;
    onApply(unitsToApply);
  };

  const selectedCount = Object.values(selectedFields).filter(Boolean).length;

  return (
    <Modal open={show} onClose={onClose} size="large" className="update-units-modal">
      {/* <Modal.Header>
        <Icon name="edit" />
        {t("update_units_in_forms") || "Modificar unidades en formularios existentes"}
      </Modal.Header> */}
      <Modal.Content scrolling>
        <div className="update-units-modal__title">{t("update_units_in_forms") || "Modificar unidades"}</div>
        {/* <Message info>
          <Message.Header>{t("select_fields_to_update") || "Selecciona los campos a modificar"}</Message.Header>
          <p>
            {t("update_units_description") ||
              "Selecciona los campos y las unidades que deseas aplicar a todos los formularios existentes del año."}
          </p>
        </Message> */}

        <div style={{ marginBottom: "15px" }}>
          <Button size="small" className="btn-select-all" onClick={handleSelectAll}>
            <Icon name="check square" /> {t("select_all") || "Seleccionar todos"}
          </Button>
          <Button size="small" className="btn-deselect-all" onClick={handleDeselectAll}>
            <Icon name="square outline" /> {t("deselect_all") || "Deseleccionar todos"}
          </Button>
          <span style={{ marginLeft: "15px", color: "#666" }}>
            {selectedCount} {t("fields_selected") || "campos seleccionados"}
          </span>
        </div>

        <Form>
          <div className="update-units-grid">
            {wasteFields.map((field) => (
              <div key={field.key} className="update-unit-row">
                <Checkbox
                  checked={selectedFields[field.key] || false}
                  onChange={(_, data) => handleFieldToggle(field.key, data.checked)}
                  label={field.label}
                  className="field-checkbox"
                />
                {selectedFields[field.key] && (
                  <Form.Dropdown
                    placeholder={t("select_unit")}
                    options={determineWasteUnits(field.key)}
                    selection
                    search
                    compact
                    value={fieldUnits[field.key] || ""}
                    onChange={(_, data) => handleUnitChange(field.key, data.value)}
                    className="unit-dropdown"
                  />
                )}
              </div>
            ))}
          </div>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button
          primary
          onClick={handleApply}
          disabled={selectedCount === 0 || isLoading}
          loading={isLoading}
        >
          <Icon name="save" />
          {t("apply_to_forms") || `Aplicar a formularios (${selectedCount})`}
        </Button>
        <Button secondary onClick={onClose} disabled={isLoading}>
          {t("cancel")}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
